import { useState, useCallback, useEffect, useRef } from "react";
import {
  importLoteFile,
  importLoteFileAsync,
  pollImportStatus,
} from "modules/finops/conciliaciones/services/loteService";
import { validateExcelLote } from "../utils/validateExcelLote";

export const useFormLote = () => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [validationErrors, setValidationErrors] = useState([]);
  const [validating, setValidating] = useState(false);
  const [importProgress, setImportProgress] = useState(0);
  const [duplicateInFile, setDuplicateInFile] = useState([]);
  const [duplicateInDatabase, setDuplicateInDatabase] = useState([]);
  const [validationDetails, setValidationDetails] = useState(null);
  const [resumenTipos, setResumenTipos] = useState({ tipo01: 0, tipo02: 0 });
  const [jobId, setJobId] = useState(null);
  const [importStatus, setImportStatus] = useState("idle");
  const [processedRows, setProcessedRows] = useState(0);
  const [totalRows, setTotalRows] = useState(0);
  const [isAsyncMode, setIsAsyncMode] = useState(false);
  const pollingRef = useRef(null);
  const operationRef = useRef(0);
  const importingRef = useRef(false);

  const stopTracking = useCallback(() => {
    operationRef.current += 1;
    pollingRef.current?.cancel();
    pollingRef.current = null;
    importingRef.current = false;
  }, []);

  useEffect(() => stopTracking, [stopTracking]);

  const cancelPolling = useCallback(() => {
    stopTracking();
    setImporting(false);
  }, [stopTracking]);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const MAX_FILE_SIZE_SYNC = 40 * 1024 * 1024;
  const MAX_FILE_SIZE_ASYNC = 500 * 1024 * 1024;

  const handleFileChange = async (file) => {
    if (importingRef.current) return;

    const validExtensions = ["xlsx", "xls"];
    const fileExtension = file.name.split(".").pop().toLowerCase();

    if (!validExtensions.includes(fileExtension)) {
      setSnackbar({
        open: true,
        message: "Formato de archivo no válido. Solo se permiten .xlsx o .xls",
        severity: "error",
      });
      return;
    }

    const isLargeFile = file.size > MAX_FILE_SIZE_SYNC;
    setIsAsyncMode(isLargeFile);

    const maxSize = isLargeFile ? MAX_FILE_SIZE_ASYNC : MAX_FILE_SIZE_SYNC;
    if (file.size > maxSize) {
      setSnackbar({
        open: true,
        message: `El archivo es demasiado grande. Máximo ${maxSize / (1024 * 1024)}MB permitidos`,
        severity: "error",
      });
      return;
    }

    setSelectedFile(file);
    setValidationErrors([]);
    setDuplicateInFile([]);
    setDuplicateInDatabase([]);
    setValidationDetails(null);
    setResumenTipos({ tipo01: 0, tipo02: 0 });
    setValidating(true);
    setImportProgress(0);
    setImportStatus("idle");

    try {
      const validation = await validateExcelLote(file);

      setValidationDetails({
        totalRows: validation.totalRows,
        validRows: validation.validRows,
        invalidRows: validation.totalRows - validation.validRows,
      });

      setDuplicateInFile(validation.duplicatesInFile || []);
      setResumenTipos(validation.resumenTipos || { tipo01: 0, tipo02: 0 });

      if (validation.isValid) {
        setValidationErrors([]);
        setDuplicateInDatabase([]);

        const modeMessage = isLargeFile
          ? " (modo asíncrono para archivos grandes)"
          : " (modo sincrónico)";

        setSnackbar({
          open: true,
          message: `✅ Archivo validado: ${file.name}${modeMessage} - ${validation.validRows} fila(s) válida(s)`,
          severity: "success",
        });
      } else {
        setValidationErrors(validation.errors);
        setSnackbar({
          open: true,
          message: `❌ El archivo tiene ${validation.errors.length} error(es). Revise el detalle.`,
          severity: "error",
        });
      }
    } catch (error) {
      console.error("❌ Error en validación:", error);
      setValidationErrors([error.message || "Error al validar el archivo"]);
      setSnackbar({
        open: true,
        message: "Error al validar el archivo: " + (error.message || "Error desconocido"),
        severity: "error",
      });
    } finally {
      setValidating(false);
      setImportProgress(0);
    }
  };

  const handleImport = async (onImportSuccess, onError) => {
    if (importingRef.current || validating) return;

    if (!selectedFile) {
      setSnackbar({
        open: true,
        message: "Seleccione un archivo para importar",
        severity: "error",
      });
      return;
    }

    if (
      validationErrors.length > 0 ||
      duplicateInFile.length > 0 ||
      duplicateInDatabase.length > 0
    ) {
      setSnackbar({
        open: true,
        message: "El archivo tiene errores de validación. Corríjalos antes de importar.",
        severity: "error",
      });
      return;
    }

    importingRef.current = true;
    const operation = ++operationRef.current;
    const isCurrent = () => operation === operationRef.current;
    const finish = () => {
      importingRef.current = false;
      pollingRef.current = null;
      setImporting(false);
    };
    let tracking = false;
    setImporting(true);
    setShowConfirmModal(true);
    setImportResult(null);
    setImportProgress(0);
    setImportStatus("uploading");
    setProcessedRows(0);
    setTotalRows(0);

    try {
      let result;
      const isLargeFile = selectedFile.size > MAX_FILE_SIZE_SYNC;

      if (isLargeFile) {
        setImportStatus("uploading");
        setSnackbar({
          open: true,
          message: "Subiendo archivo grande... Esto puede tomar varios minutos",
          severity: "info",
        });

        result = await importLoteFileAsync(selectedFile, null, (progress) => {
          if (isCurrent()) setImportProgress(progress);
        });

        if (!isCurrent()) return;

        if (result.success) {
          setJobId(result.jobId);
          setImportStatus("processing");
          setImportProgress(0);

          tracking = true;
          const polling = pollImportStatus(
            result.jobId,
            (progress, processed, total, batchInfo) => {
              if (!isCurrent()) return;
              setImportProgress(progress);
              setProcessedRows(processed);
              setTotalRows(total);

              setSnackbar({
                open: true,
                message: `Procesando: ${progress}% (${processed.toLocaleString()}/${total.toLocaleString()} registros) - Lote ${
                  batchInfo.batch
                }/${batchInfo.totalBatches}`,
                severity: "info",
              });
            },
            (finalResult) => {
              if (!isCurrent()) return;
              finish();
              setImportStatus("completed");
              setImportProgress(100);

              const normalized = {
                success: true,
                importedCount: finalResult.importedCount || 0,
                errorsCount: finalResult.errorsCount || 0,
                totalRows: finalResult.totalRows || 0,
                message: finalResult.message || "Importación completada exitosamente",
                validationErrors: finalResult.errorDetails ? [finalResult.errorDetails] : [],
                validationSummary: {
                  totalRows: finalResult.totalRows || 0,
                  validRows: finalResult.importedCount || 0,
                  invalidRows: finalResult.errorsCount || 0,
                },
              };

              setImportResult(normalized);
              if (onImportSuccess) onImportSuccess(normalized);
            },
            (error) => {
              if (!isCurrent()) return;
              finish();
              setImportStatus("error");
              const errMsg =
                typeof error === "string"
                  ? error
                  : error?.message || "Error en la importación asíncrona";

              setImportResult({ success: false, error: errMsg, isTimeout: false });
              if (onError) onError(errMsg);
            },
            {
              interval: 3000,
              maxAttempts: 7200,
              onStatusChange: () => {},
            }
          );

          if (isCurrent() && importingRef.current) pollingRef.current = polling;
          else polling.cancel();
        } else {
          setImportStatus("error");
          setImportResult({
            success: false,
            error: result.error,
            details: result.details,
          });
          if (onError) onError(result.error);
        }
      } else {
        setImportStatus("uploading");

        result = await importLoteFile(selectedFile, null, (progress) => {
          if (isCurrent()) setImportProgress(progress);
        });

        if (!isCurrent()) return;
        setImportResult(result);

        if (result.success) {
          setImportStatus("completed");
          setImportProgress(100);
          if (onImportSuccess) onImportSuccess(result);
        } else {
          setImportStatus("error");
          const errorMsg = result.error || "Error al importar el archivo";
          if (onError) onError(errorMsg);
        }
      }
    } catch (error) {
      if (!isCurrent()) return;
      tracking = false;
      console.error("❌ Error en importación:", error);
      setImportStatus("error");

      const errorMsg = error.message || "Error al importar el archivo";
      setImportResult({
        success: false,
        error: errorMsg,
        details: error.response?.data || null,
        status: error.response?.status || 500,
      });

      if (onError) onError(errorMsg);
    } finally {
      if (isCurrent() && !tracking) finish();
    }
  };

  const handleCloseModal = () => {
    if (importingRef.current) return;
    cancelPolling();
    setShowConfirmModal(false);

    if (importResult?.success) {
      setSelectedFile(null);
      setValidationErrors([]);
      setDuplicateInFile([]);
      setDuplicateInDatabase([]);
      setValidationDetails(null);
      setResumenTipos({ tipo01: 0, tipo02: 0 });
      setImportProgress(0);
      setJobId(null);
      setImportStatus("idle");
      setProcessedRows(0);
      setTotalRows(0);

      const fileInput = document.getElementById("file-input");
      if (fileInput) fileInput.value = "";
    }
  };

  const clearSelectedFile = () => {
    cancelPolling();
    setImportResult(null);
    setShowConfirmModal(false);

    setSelectedFile(null);
    setValidationErrors([]);
    setDuplicateInFile([]);
    setDuplicateInDatabase([]);
    setValidationDetails(null);
    setResumenTipos({ tipo01: 0, tipo02: 0 });
    setImportProgress(0);
    setJobId(null);
    setImportStatus("idle");
    setProcessedRows(0);
    setTotalRows(0);

    const fileInput = document.getElementById("file-input");
    if (fileInput) fileInput.value = "";
  };

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const resetForm = useCallback(() => {
    cancelPolling();

    setSelectedFile(null);
    setImporting(false);
    setImportResult(null);
    setShowConfirmModal(false);
    setValidationErrors([]);
    setValidating(false);
    setImportProgress(0);
    setDuplicateInFile([]);
    setDuplicateInDatabase([]);
    setValidationDetails(null);
    setResumenTipos({ tipo01: 0, tipo02: 0 });
    setJobId(null);
    setImportStatus("idle");
    setProcessedRows(0);
    setTotalRows(0);
    setSnackbar({ open: false, message: "", severity: "success" });

    const fileInput = document.getElementById("file-input");
    if (fileInput) fileInput.value = "";
  }, [cancelPolling]);

  return {
    selectedFile,
    importing,
    importResult,
    showConfirmModal,
    validationErrors,
    validating,
    snackbar,
    importProgress,
    duplicateInFile,
    duplicateInDatabase,
    validationDetails,
    resumenTipos,
    jobId,
    importStatus,
    processedRows,
    totalRows,
    isAsyncMode,
    handleFileChange,
    handleImport,
    handleCloseModal,
    clearSelectedFile,
    handleCloseSnackbar,
    setSnackbar,
    resetForm,
    cancelPolling,
  };
};
